import { Button, Form, Switch, Tooltip } from 'antd';
import { useContext, useState } from 'react';

import { UserContext } from 'app/context/UserContext/UserContext';
import type { DayOfWeek } from 'app/typings/core/common/enum/dayOfWeek';

import styles from '../PricingDrawer.module.scss';
import { usePackPricingScores } from '../hooks/usePackPricingScores';

import { LowPricingScoreModal } from './LowPricingScoreModal/LowPricingScoreModal';
import type { DefaultDiscountMode } from './RoomDiscount/hooks/useRoomDiscountLogic';
import { hasRoomDiscountOverMax } from './RoomDiscount/roomDiscountRules';
import { hasDiscountRule } from './hasDiscountRule';

type PricingFooterProps = {
  handleClose: () => void;
  handleSave: () => void;
};

export const PricingFooter = ({
  handleClose,
  handleSave,
}: PricingFooterProps) => {
  const { isAdmin } = useContext(UserContext);

  const form = Form.useFormInstance<{
    averageRoomPrice: number;
    defaultDiscountMode?: DefaultDiscountMode;
    defaultNightRoomDiscount?: number | null;
    defaultNightRoomDiscountDays?: Partial<Record<DayOfWeek, number>> | null;
    isDefaultDiscountEnabled?: boolean;
    lastMinuteNightRoomDiscountRate?: number | null;
    lastMinuteNightRoomDiscountDays?: number | null;
  }>();

  const averageRoomPrice = Form.useWatch('averageRoomPrice', form);
  const isDefaultDiscountEnabled = Form.useWatch(
    'isDefaultDiscountEnabled',
    form
  );
  const defaultDiscountMode = Form.useWatch('defaultDiscountMode', form);
  const defaultNightRoomDiscount = Form.useWatch(
    'defaultNightRoomDiscount',
    form
  );
  const defaultNightRoomDiscountDays = Form.useWatch(
    'defaultNightRoomDiscountDays',
    form
  );
  const lastMinuteNightRoomDiscountRate = Form.useWatch(
    'lastMinuteNightRoomDiscountRate',
    form
  );
  const lastMinuteNightRoomDiscountDays = Form.useWatch(
    'lastMinuteNightRoomDiscountDays',
    form
  );

  const isMissingDiscountRule = !hasDiscountRule({
    isDefaultDiscountEnabled,
    lastMinuteNightRoomDiscountRate,
    lastMinuteNightRoomDiscountDays,
  });

  const isRoomDiscountOverMax = hasRoomDiscountOverMax({
    defaultDiscountMode,
    defaultNightRoomDiscount: isDefaultDiscountEnabled
      ? defaultNightRoomDiscount
      : null,
    defaultNightRoomDiscountDays: isDefaultDiscountEnabled
      ? defaultNightRoomDiscountDays
      : null,
    lastMinuteNightRoomDiscountRate,
  });

  const { lowScorePacks, hasLowScore } = usePackPricingScores();

  const [isLowScoreModalOpen, setIsLowScoreModalOpen] = useState(false);

  const handleSaveClick = () => {
    if (isAdmin && hasLowScore) {
      setIsLowScoreModalOpen(true);

      return;
    }

    handleSave();
  };

  const handleConfirmSaveAnyway = () => {
    setIsLowScoreModalOpen(false);
    handleSave();
  };

  return (
    <div className={styles.footer}>
      <Form.Item shouldUpdate noStyle>
        {() => {
          const hasFormErrors = !!form
            .getFieldsError()
            .filter(({ errors }) => errors.length).length;

          const isDisabled =
            hasFormErrors || !averageRoomPrice || isRoomDiscountOverMax;
          const isSaveDisabled =
            isDisabled || isMissingDiscountRule || (!isAdmin && hasLowScore);

          return (
            <>
              <Button onClick={handleClose}>Cancel</Button>
              <div className={styles.footerRight}>
                <div className={styles.overrideWrapper}>
                  <span className={styles.overrideLabel}>
                    Override manual data
                  </span>
                  <Form.Item
                    name="overrideManualData"
                    valuePropName="checked"
                    noStyle
                  >
                    <Switch
                      aria-label="Override manual data"
                      disabled={isDisabled}
                    />
                  </Form.Item>
                </div>
                <Tooltip
                  title={
                    isMissingDiscountRule
                      ? 'Enable a default or a last minute discount'
                      : undefined
                  }
                >
                  <Button
                    type="primary"
                    onClick={handleSaveClick}
                    disabled={isSaveDisabled}
                  >
                    Save discounts
                  </Button>
                </Tooltip>
              </div>
            </>
          );
        }}
      </Form.Item>

      <LowPricingScoreModal
        isOpened={isLowScoreModalOpen}
        packNames={lowScorePacks.map((pack) => pack.name)}
        handleCancel={() => setIsLowScoreModalOpen(false)}
        handleSaveAnyway={handleConfirmSaveAnyway}
      />
    </div>
  );
};
